import { DayPicker } from '@/ui/common/Pickers/DayPicker';
import { DAY_PICKER_STYLES } from '../../AvailabilityCalendar';
import { cn } from '@/service/utils/cn';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchAvailabilityStore } from '@/service/state/SearchAvailability/useSearchAvailabilityStore';
import { isPastDay } from '@/service/utils/isPastDay';
import { TActivity } from '@/domain/Activities.domain';
import { SearchCard } from './SearchCard';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { newDateFromString } from '@/service/utils/getDateByTimezone';
import { useAttractionsStore } from '@/service/state/Attractions/useAttractionsStore';
import { useAvailabilityStore } from '@/service/state/Availability/useAvailabilityStore';
import { TicketsWidget } from '../../TicketsWidget/TicketsWidget';
import { ArrowLeftIcon } from '@/ui/common/Icons/ArrowLeftIcon';
import { ArrowRightIcon } from '@/ui/common/Icons/ArrowRightIcon';
import { format, setDate } from 'date-fns';
import { SEARCH_FORMAT, SERVER_FORMAT_DATE } from '@/service/constants/dates';
import { useCartStore } from '@/service/state/Cart/useCartStore';
import { Toast } from '@/ui/common/Toast/Toast';
import { ToastScreen } from '../../CartContent/core/ToastScreen';
import Link from 'next/link';
import { ECartActionType } from '@/service/state/Cart/domain';
import { TicketsContainer } from './TicketsContainer';
import { isPastTime } from '@/service/utils/isPastTime';
import { TCategory } from '@/domain/Categories.domain';
import { paramsToString } from '@/service/state/SearchAvailability/utils';
import { Accordion } from '@/ui/common/Accordion/Accordion';
import { CheckboxInput } from '@/ui/common/Inputs/CheckboxInput';

type TSearchDateProps = {
    activities: TActivity[];
    partnerSlug: string;
    categoriesList?: TCategory[];
    selectedCategory?: TCategory | null;
};

const FILTER = 'filter';
const CATEGORY = 'category';

export const SearchDate = ({
    activities,
    partnerSlug,
    categoriesList,
    selectedCategory,
}: TSearchDateProps) => {
    const today = useMemo(() => new Date(), []);
    const { months, selectedDate, isMonthLoading, initDate, selectDate, addMonth } =
        useSearchAvailabilityStore();
    const { address, show_reviews, timezone, phone } = usePartnerStore();
    const pathname = usePathname();
    const { success } = useCartStore();
    const checkoutLink = useMemo(() => `/${partnerSlug}/checkout/`, [partnerSlug]);
    const [pageMonth, setPageMonth] = useState<Date | null>(new Date());
    const [selectedActivityId, setSelectedActivityId] = useState('');
    const [filter, setFilter] = useState<string[]>(selectedCategory ? [selectedCategory.slug] : []);
    const router = useRouter();
    const searchParams = useSearchParams();

    const selectedMonth = !isMonthLoading && !!pageMonth && months?.[pageMonth.getMonth()]?.data;
    const { isSchedulesLoading, getSchedules } = useSearchAvailabilityStore();
    const currentSchedules = getSchedules();
    const prices = useAttractionsStore((state) => state.prices);
    const images = useAttractionsStore((state) => state.images);
    const {
        selectSchedule,
        selectedSchedule,
        initDate: availInitDate,
        clearSelectedSchedule,
    } = useAvailabilityStore();
    const selectedActivity = activities.find(({ id }) => id === selectedActivityId);
    const dateQuery = selectedDate ? `?date=${format(selectedDate, SERVER_FORMAT_DATE)}` : '';

    const isDisabled = useCallback(
        (date: Date) =>
            isPastDay(date) ||
            !selectedMonth ||
            !selectedMonth?.includes(date.getDate().toString()),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedDate, selectedMonth]
    );

    useEffect(() => {
        initDate(
            pathname.replace(/\//g, '').replace('lightframe', ''),
            activities.map(({ id }) => id).join()
        );
        selectDate(selectedDate);
        if (selectedDate) {
            setPageMonth(selectedDate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {}, []);

    const handleClick = (tripId: string) => (scheduleId: number) => {
        if (selectedDate) {
            setSelectedActivityId(tripId);
            availInitDate(selectedDate, tripId);
            selectSchedule(scheduleId);
        }
    };

    const selectedMonthIndex = selectedDate && selectedDate.getMonth();
    const selectedDayIndex = selectedDate && selectedDate.getDate().toString();
    const currentMonth =
        selectedMonthIndex && months[selectedMonthIndex]?.data.sort((a, b) => +a - +b);

    const changeDay = (mode: 'prev' | 'next') => {
        const currentDay = selectedDate && selectedDate.getDate().toString();

        if (selectedMonthIndex && currentMonth && currentDay) {
            const newDay =
                mode === 'prev'
                    ? currentMonth[currentMonth.findIndex((day) => day === currentDay) - 1]
                    : currentMonth[currentMonth.findIndex((day) => day === currentDay) + 1];

            if (newDay) {
                selectDate(setDate(selectedDate, +newDay));
            }
        }
    };

    const isFirstDay =
        !!currentMonth &&
        selectedDayIndex &&
        (currentMonth?.findIndex((day) => day === selectedDayIndex) ?? -1) <= 0;

    const isLastDay =
        typeof selectedMonthIndex !== 'undefined' &&
        typeof selectedDayIndex !== 'undefined' &&
        currentMonth &&
        (currentMonth?.findIndex((day) => day === selectedDayIndex) ?? -1) ===
            (currentMonth?.length ?? 0) - 1;

    const updateParams = (list: string[]) => {
        const params = new URLSearchParams(searchParams.toString());
        const isCategory = params.get(CATEGORY);

        const updateParams = isCategory ? router.push : router.replace;

        if (isCategory) {
            params.delete(CATEGORY);
        }

        if (!list.length) {
            params.set(FILTER, 'all');

            updateParams(paramsToString(params), { scroll: false });

            return;
        }

        params.set(FILTER, Array.from(new Set(list)).join(','));

        updateParams(paramsToString(params), { scroll: false });
    };

    const selectFilter = (slug: string, skipStateUpdate?: boolean) => {
        const newCategory = categoriesList?.find((category) => category.slug === slug);

        if (slug === 'all') {
            setFilter([]);
            updateParams(['all']);
        }

        if (newCategory) {
            const isSelected = filter.includes(slug);

            setFilter((prev) => {
                const newList = isSelected
                    ? [...prev].filter((item) => item !== slug)
                    : [...prev, slug];

                const listToUpdate = skipStateUpdate ? prev : newList;

                updateParams(listToUpdate);

                return listToUpdate;
            });
        }
    };

    const filterTrips =
        !!filter.length &&
        !!categoriesList &&
        categoriesList
            .filter((category) => filter.includes(category.slug))
            .map((category) => category.trips)
            .flat();

    const listToShow =
        !!filterTrips && filterTrips.length
            ? activities.filter((activity) => filterTrips.includes(activity.id))
            : activities;

    const isAvailList =
        isSchedulesLoading ||
        (!!currentSchedules &&
            Object.values(currentSchedules).some((schedules) =>
                schedules.some(
                    (schedule) =>
                        schedule.schedules.some(
                            ({ is_avail, call_to_booking, time }) =>
                                is_avail || (call_to_booking && !isPastTime(time, timezone))
                        ) && listToShow.find((trip) => +trip.id === schedule.trip_id)
                )
            ));

    useEffect(() => {
        if (searchParams) {
            const queryCategories = searchParams.get(FILTER)?.split(',');
            const queryCategory = searchParams.get(CATEGORY);

            if (queryCategory) {
                selectFilter(queryCategory, true);

                return;
            }

            if (queryCategories && queryCategories.length) {
                queryCategories.forEach((category) => selectFilter(category));
            }

            if (!queryCategories) {
                selectFilter('all', true);
            }
        }

        return () => {
            const params = new URLSearchParams(searchParams.toString());
            params.delete(FILTER);
            router.replace(paramsToString(params), { scroll: false });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="wrapper flex min-h-screen flex-col px-3 pb-14 md:flex-row md:px-8 md:pb-10">
            <Toast
                show={!!success.message && success.actionType === ECartActionType.ADD_ITEM_TO_CART}
                screen={
                    <ToastScreen
                        title={success.message}
                        description={
                            <Link
                                href={checkoutLink}
                                className="tx-large-medium block w-full text-brand underline"
                            >
                                Checkout now
                            </Link>
                        }
                    />
                }
            />
            <div className="relative mb-5 w-full md:w-[59%] md:min-w-[364px] md:max-w-[416px]">
                <div
                    className={cn(
                        'rounded-2xl bg-white px-4 pb-4 shadow-lightCard dark:bg-dark-3 dark:shadow-darkCard md:pb-[18px] md:pt-[2px] lg:px-[26px]',
                        isMonthLoading ? 'loading' : ''
                    )}
                    id="calendar"
                    data-test-id="avail-calendar"
                >
                    <DayPicker
                        mode="single"
                        classNames={DAY_PICKER_STYLES}
                        selected={!isMonthLoading ? (selectedDate as Date) : undefined}
                        onSelect={(date) => {
                            selectDate(date);
                        }}
                        disabled={isDisabled}
                        onMonthChange={(newDate) => {
                            addMonth(newDate);
                            setPageMonth(newDate);
                        }}
                        month={pageMonth ? newDateFromString(pageMonth) : new Date()}
                        fromMonth={today}
                        today={today}
                        isLoading={isMonthLoading}
                    />
                    <div className="tx-large-medium mt-2.5 flex justify-center text-gray-1 dark:text-white md:mt-4">
                        <span className="mr-5 flex items-center before:mr-2 before:block before:h-4 before:w-4 before:rounded-2xl before:bg-brand before:opacity-70 before:content-['']">
                            Selected
                        </span>
                        <span className="mr-5 flex items-center before:mr-2 before:block before:h-4 before:w-4 before:rounded-2xl before:bg-brand-light before:opacity-70 before:content-[''] dark:before:bg-brand-dark">
                            Available
                        </span>
                    </div>
                </div>
                {!!categoriesList?.length && (
                    <Accordion
                        clearStyle
                        data={[
                            {
                                title: 'Filter By Category',
                                id: 'filter',
                                content: (
                                    <>
                                        {categoriesList.map((category) => (
                                            <CheckboxInput
                                                dataTestId="pay-in-full"
                                                label={category.title}
                                                checked={filter.includes(category.slug)}
                                                handleChange={() => selectFilter(category.slug)}
                                                className="mb-1"
                                                key={category.id}
                                            />
                                        ))}
                                    </>
                                ),
                            },
                        ]}
                    />
                )}
            </div>
            <div className="w-full md:ml-4">
                <div className="tx-heading-4 mb-2 flex h-12 items-center justify-between rounded-3xl border border-brand bg-blue-9 px-3 py-3 dark:bg-blue-1 lg:h-[50px]">
                    <button
                        type="button"
                        disabled={isSchedulesLoading}
                        onClick={() => changeDay('prev')}
                        className={cn(
                            'tx-medium-semibold flex shrink-0 items-center overflow-hidden text-ellipsis whitespace-nowrap text-dark-1 hover:underline',
                            isFirstDay && 'invisible'
                        )}
                    >
                        <ArrowLeftIcon className="h-3 w-3 min-w-[16px] [&_path]:fill-dark-1" />
                        Prev day
                    </button>
                    <span
                        className={cn(
                            'overflow-hidden text-ellipsis whitespace-nowrap text-dark-3 dark:text-white',
                            isSchedulesLoading && 'animate-pulse blur-sm'
                        )}
                    >
                        {!!selectedDate && format(selectedDate, SEARCH_FORMAT)}
                    </span>
                    <button
                        type="button"
                        disabled={isSchedulesLoading}
                        onClick={() => changeDay('next')}
                        className={cn(
                            'tx-medium-semibold flex shrink-0 items-center overflow-hidden text-ellipsis whitespace-nowrap text-dark-1 hover:underline',
                            isLastDay && 'invisible'
                        )}
                    >
                        Next day
                        <ArrowRightIcon className="h-3 w-3 min-w-[16px] [&_path]:fill-dark-1" />
                    </button>
                </div>
                {isAvailList ? (
                    listToShow.map((activity) => (
                        <SearchCard
                            pathname={pathname}
                            activity={activity}
                            address={address}
                            key={activity.id}
                            showReviews={show_reviews}
                            isLoading={isSchedulesLoading}
                            schedules={currentSchedules ? currentSchedules[activity.id] : undefined}
                            price={prices[activity.id]}
                            selectSchedule={handleClick(activity.id)}
                            dateQuery={dateQuery}
                            images={images[`parent_${activity.id}`]}
                            partnerPhone={phone}
                        />
                    ))
                ) : (
                    <div className="tx-heading-2 mt-6 text-center text-dark-3 dark:text-white">
                        Sorry, there is no availability for this day.
                        <p className="tx-large-medium text-dark-1">Please choose another day.</p>
                    </div>
                )}
            </div>
            {!!selectedSchedule && !!selectedActivity && (
                <TicketsContainer onClose={clearSelectedSchedule} widthFull>
                    <TicketsWidget
                        tripName={selectedActivity.name}
                        agreement={selectedActivity.agreement}
                        calendarStatus={selectedActivity.statuses}
                        showCalendarStatuses
                    />
                </TicketsContainer>
            )}
        </div>
    );
};
